import *as THREE from 'three'
import { StatefulFreestyleItem, StatefullFreestyleItemCounter } from "types/freestyle.types"
import { FreestyleItemButton, FreestyleItemAction, FreestyleItemCond } from 'types/data'
import { Effect } from 'lib/sound'
import dynamicWaterPressure from 'lib/dynamicWaterPressure'
import { debounce } from 'throttle-debounce'
import { NativeAnimationsItem } from './Freestyle'
import { AnimationAction } from 'three/src/animation/AnimationAction'
import DynamicVacuum from 'lib/dynamicVacuum'

interface Props { 
  data: StatefulFreestyleItem,
  nativeAnimations: NativeAnimationsItem[]
  onIncrement: (dir: 1 | -1) => void
  getActionByName: (name?: string) => AnimationAction | undefined
  isConditionDone: (condition: FreestyleItemCond, d: any) => boolean | undefined
  
}

export interface FActionObject {
  animationAction?: AnimationAction 
  clipName?: string 
  speed?: number
  greenButton: boolean
  buttons: FreestyleItemButton[]
  type: string
  rootItem: FreestyleItemAction
  sounds: Effect[]
  nativeAnimation?: dynamicWaterPressure
  secondaryAction?: AnimationAction
}

export default class FreestyleAction {
  props: Props
  actions: FActionObject[]
  includedButtons: string[]
  includesGreenButton = false
  counter?: StatefullFreestyleItemCounter
  soundEffects: Effect[] = []
  mainNativeAnimation?: dynamicWaterPressure | DynamicVacuum
  
  constructor(props: Props) {
    this.props = props
    
    const { data } = props

    if(data.counter) {
      this.counter = data.counter

      this.soundEffects = data.counter.effects.map(item => 
        new Effect(item.sound, () => {}, true)
      )
    }

    const actionButtonsSounds = data.actions
      .filter(i => i.buttons.some(b => b.sound))
      .map(i => i.buttons
          .filter(b => b.sound)
          .map(b => new Effect(b.sound!, () => {}, true))
        )


    //@ts-ignore
    this.soundEffects = [
      ...(this.soundEffects || []), 
      ...(actionButtonsSounds[0] || [])
    ]

    

    this.actions = data.actions.map(item => {
      const { nativeAnimations, getActionByName } = this.props
      
      const animationAction = getActionByName(item.clipName)
      const secondaryAction = getActionByName(item.secondClip)

      
      if(animationAction) {
        
        animationAction.timeScale = item.actionspeed || animationAction.timeScale

        animationAction.loop = THREE.LoopOnce
        animationAction.clampWhenFinished = true
      }

      if(secondaryAction) {
        
        secondaryAction.timeScale = item.actionspeed || secondaryAction.timeScale

        secondaryAction.loop = THREE.LoopOnce
        secondaryAction.clampWhenFinished = true
      }

      let nativeAnimation;
      
      if(item.nativeAnimation) {
        nativeAnimation = nativeAnimations.find(i => i.name === item.nativeAnimation!.name)!.animation
        this.mainNativeAnimation = nativeAnimation
      }      
      
      
      return ({
        greenButton: Boolean(item.secondary),
        rootItem: item,
        clipName: item.clipName,
        speed: item.actionspeed,
        buttons: item.buttons,
        type: item.type,
        animationAction,
        secondaryAction,
        nativeAnimation,
        sounds: item.buttons
          .filter(b => b.sound)
          .map(b => new Effect(b.sound!, () => {}, !b.syncSound))
      });
    }) 

    
    this.includedButtons = this.actions.reduce((acc: string[], item) => [
      ...acc,
      ...item.buttons.map(item => item.name)
    ], [])

    this.onChangeCounterSound = debounce(500, this.onChangeCounterSound)
  }

  dispose() {
    this.actions.forEach(item => {
      if(item.secondaryAction) {
        item.secondaryAction.stop()
      }
      if(item.animationAction) {
        item.animationAction.stop()
      } 
      if(item.nativeAnimation) {
        item.nativeAnimation.stop()
      } if (item.sounds) {
        item.sounds.forEach(item => item.stop(true))
        this.soundEffects.forEach(item => item.stop(true))
      }
    })
  }

  
  playActionClip = (action: FActionObject, button: FreestyleItemButton ) => {
    const { animationAction, secondaryAction, nativeAnimation } = action
    const targetSound = FreestyleAction.getButtonSoundsByAction(action, button)
    
    
    if(animationAction)  { 
      //@ts-ignore
      animationAction.play()
      animationAction.paused = false

      FreestyleAction.setDirToAnimationAction(animationAction, button.dir)
    }

    if(secondaryAction)  {
      // console.log(animationAction.time)

      secondaryAction.play()
      secondaryAction.paused = false

      FreestyleAction.setDirToAnimationAction(secondaryAction, button.dir)
    }
    
    if(targetSound && targetSound.syncSound) {
      if(animationAction) {
        //@ts-ignore
        const animationProgress = animationAction.time / animationAction._clip.duration
        
        targetSound.sound.play(true, button.dir === -1 ? 1 - animationProgress : animationProgress)
      }
    }

    if(nativeAnimation) {
      if(nativeAnimation instanceof dynamicWaterPressure) {
        if(button.dir === 1) {
          this.soundEffects[this.counter!.current]!.play()
        } else {
          this.soundEffects.forEach(item => item.stop())
        }
      }


      if(nativeAnimation instanceof DynamicVacuum) {
        
        if(button.dir === 1) {
          this.soundEffects.forEach(i => i.play())
        } else {
          this.soundEffects.forEach(item => item.stop())
        }
      }
      
      if(button.dir === 1) {
        nativeAnimation.setActive(true)
        nativeAnimation.play()
      } else {
        nativeAnimation.pause()
      }

    }

  }

  stopActionClip = (action: FActionObject) => {
    const { animationAction, secondaryAction } = action
    const targetSounds = action.buttons
      .filter(item => item.sound && item.syncSound)
      .map(item => FreestyleAction.getButtonSoundsByAction(action, item)!)
    
    if(animationAction) {
      animationAction.paused = true
    }
    if(secondaryAction) {
      secondaryAction.paused = true
    }

    if(targetSounds) {
      targetSounds.forEach(({ sound }) => sound.stop(true))
    }
  }

  onEmptyMouseUp() {
    this.actions.forEach(item => {
      switch(item.type) {
        case 'hold': 
          this.stopActionClip(item)

        break
      }
    })
  }

  onIncrement(button: FreestyleItemButton) {
    requestAnimationFrame(
      () => this.props.onIncrement((button.dir as 1 | -1))
    )
  }
  
  onChangeCounter(newCounterState: StatefullFreestyleItemCounter) {
    this.counter = {
      ...newCounterState,
      effects: this.counter!.effects
    }

    this.onChangeCounterNativeAnimation()
    this.onChangeCounterSound()
  }
  
  onChangeCounterNativeAnimation() {
    if(this.mainNativeAnimation) {
      const { effects, current } = this.counter!
      
      const currentMode = effects[current].state

      this.mainNativeAnimation.setMode(currentMode)
    }
  }
  
  onChangeCounterSound = () => {
    const { effects, current } = this.counter!
    
    if(this.mainNativeAnimation instanceof dynamicWaterPressure) {
      if(this.mainNativeAnimation!.paused) {
        return
      }
    } 
    
    const targetSound = this.soundEffects.find(item => item.src === effects[current].sound)
    this.soundEffects.forEach((item) => item !== targetSound && item.stop(false))
  
    targetSound!.play()
  }
  
  onButtonEvent(clickedButton: string | undefined, greenPresed: boolean, eventType: 'down' | 'up' | 'move') {
    
    const targetAction = this.actions.find(item => 
      item.buttons.find(b => b.name === clickedButton && item.greenButton === greenPresed)
      )
      
    const targetButton = targetAction ? targetAction.buttons.find(b => b.name === clickedButton)! : undefined


    
    const isConditionsDone = this.props.data.conditions ? 
      this.props.data.conditions.every(item => this.props.isConditionDone(item, targetButton ? targetButton.dir : undefined))
      :
      true
    
    if(eventType === 'up' && !clickedButton) {
      this.onEmptyMouseUp()
    }
    
    if(targetAction && isConditionsDone) {
      
      switch(targetAction.type) {
        case 'toggle': {
          if(eventType === 'down') {
              this.playActionClip(targetAction, targetButton!)
          }
          
          break         
        }
        
        case 'hold': {
          if(eventType === 'down') {
            this.playActionClip(targetAction, targetButton!)
          } else {
            this.stopActionClip(targetAction)
          }
        
          break
        }

        case 'increment': {
          if(eventType ==='down') {
            this.onIncrement(targetButton!)
          }

          break
        }
      }      

      return {
        buttons: targetAction.buttons,
        pressed: targetButton,
        action: targetAction.rootItem,
        data: this.props.data,
      }
    }
  }

  static getButtonSoundsByAction (action: FActionObject, b: FreestyleItemButton ) {
    const button = action.buttons.find(item => item.sound === b.sound || b === item)

    return button && {
      sound: action.sounds.find(item => item.src === button.sound)!,
      syncSound: button.syncSound
    }
  }

  static setDirToAnimationAction (animationAction: AnimationAction, dir: number | 1 | -1) {
    const isChangeTimeScale = !((animationAction.timeScale < 0 && dir < 0) || (animationAction.timeScale > 0 && dir > 0))

    animationAction.timeScale = animationAction.timeScale * (isChangeTimeScale ? -1 : 1)
  }
}